import createAuth0Client from '@auth0/auth0-spa-js';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { clearImpersonationToken, doStoredRequest, extractAuthProps, getAuth0ClientOptions, getImpersonateAuthProps, isTimingError, onRedirectCallback, SessionTimer, storeOriginalRequest, TIMEOUT_MARGIN, } from '../utils';
import { Auth0Context } from './auth0Context';
const Auth0Provider = ({ children, onLoginDone, config }) => {
    const initOptions = getAuth0ClientOptions(config);
    const isMounted = useRef(false);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);
    const [isAuthenticated, setIsAuthenticated] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(true);
    const [accessToken, setAccessToken] = React.useState(undefined);
    const [auth0, setAuth0] = React.useState(undefined);
    const [isSessionActive, setIsSessionActive] = React.useState(true);
    const [isImpersonating, setIsImpersonating] = React.useState(false);
    const [isImpersonateExpired, setIsImpersonateExpired] = React.useState(false);
    const [leewayError, setLeewayError] = React.useState(false);
    const authTokenTimer = new SessionTimer();
    const impersonateAuthTokenTimer = new SessionTimer();
    React.useEffect(() => {
        let auth0Client;
        const completeLogin = async (client, authProps, isRenewed) => {
            if (isMounted.current) {
                setAuth0(client);
                setIsSessionActive(true);
                setAccessToken(authProps.token);
                const tokenClaims = await client.getIdTokenClaims();
                const exp = (tokenClaims && tokenClaims.exp) || 0;
                authTokenTimer.handleExpiryTimeout(() => {
                    initAuth0(true);
                }, exp);
                const impersonateAuthProps = getImpersonateAuthProps(authProps, config.environment);
                setIsImpersonating(!!impersonateAuthProps);
                if (impersonateAuthProps) {
                    impersonateAuthTokenTimer.handleExpiryTimeout(() => {
                        setIsImpersonateExpired(true);
                    }, impersonateAuthProps.exp);
                }
                onLoginDone(impersonateAuthProps || authProps, isRenewed);
            }
        };
        const refreshLogin = async () => {
            if (isMounted.current && !document.hidden) {
                try {
                    const newAuth0Client = await createAuth0Client(initOptions);
                    const newToken = await auth0Client.getTokenSilently();
                    const oldUser = await auth0Client.getUser();
                    const newUser = await newAuth0Client.getUser();
                    const oldAuthProps = extractAuthProps(oldUser, config, accessToken);
                    const newAuthProps = extractAuthProps(newUser, config, newToken);
                    if (!oldAuthProps ||
                        !newAuthProps ||
                        oldAuthProps.userId !== newAuthProps.userId ||
                        oldAuthProps.companyId !== newAuthProps.companyId) {
                        clearImpersonationToken(config.environment, false);
                        return window.location.reload();
                    }
                    const impersonateAuthProps = getImpersonateAuthProps(newAuthProps, config.environment);
                    if (impersonateAuthProps && impersonateAuthProps.exp - Date.now() / 1000 - TIMEOUT_MARGIN <= 0) {
                        return setIsImpersonateExpired(true);
                    }
                    completeLogin(newAuth0Client, newAuthProps, true);
                }
                catch (error) {
                    console.error('The session is expired: ', error);
                    setIsSessionActive(false);
                }
            }
        };
        const initAuth0 = async (isRenewed = false) => {
            setIsLoading(true);
            auth0Client = await createAuth0Client(initOptions);
            if (window.location.search.includes('code=')) {
                try {
                    const { appState } = await auth0Client.handleRedirectCallback();
                    onRedirectCallback(appState);
                }
                catch (error) {
                    if (isTimingError(error)) {
                        setLeewayError(true);
                        return;
                    }
                }
            }
            let checkIfAuthenticated;
            try {
                checkIfAuthenticated = await auth0Client.isAuthenticated();
            }
            catch (error) {
                if (isTimingError(error)) {
                    setLeewayError(true);
                    return;
                }
            }
            setIsAuthenticated(checkIfAuthenticated);
            if (checkIfAuthenticated) {
                const token = await auth0Client.getTokenSilently();
                const user = await auth0Client.getUser();
                const authProps = extractAuthProps(user, config, token);
                if (!authProps) {
                    throw new Error('Unable to decode Authentication token.');
                }
                completeLogin(auth0Client, authProps, isRenewed);
                doStoredRequest(initOptions.client_id);
            }
            else {
                storeOriginalRequest(initOptions.client_id);
                await auth0Client.loginWithRedirect();
            }
            document.addEventListener('visibilitychange', refreshLogin);
            setIsLoading(false);
        };
        if (isMounted.current) {
            initAuth0();
        }
        return () => {
            document.removeEventListener('visibilitychange', refreshLogin);
        };
    }, []);
    const handleLogout = () => {
        auth0.logout({
            returnTo: process.env.AUTH0_LOGOUT_RETURN_URL || window.location.origin,
        });
    };
    const handleStopImpersonating = () => {
        clearImpersonationToken(config.environment);
    };
    const auth = {
        isLoading,
        isAuthenticated,
        isSessionActive,
        isImpersonateExpired,
        isImpersonating,
        stopImpersonating: handleStopImpersonating,
        logout: handleLogout,
        leewayError,
    };
    return React.createElement(Auth0Context.Provider, { value: auth }, children);
};
export default Auth0Provider;
//# sourceMappingURL=auth0.js.map